import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlug } from '@fortawesome/pro-regular-svg-icons';
import styles from './ProviderIcon.module.css';

const BASE = '/images/providers';

/**
 * Bundled brand marks, keyed by backend provider key.
 *
 * These exist because the catalog's `logo_url` is not reliably populated, and a
 * generic plug for every provider is what the integration surfaces rendered
 * before this component. Assets are official marks kept unmodified from source;
 * size and pad them, don't recolour or crop.
 *
 * Providers deliberately absent: `custom_mcp` (generic by nature) and
 * `cyber-pulse` (uses Cowbell's own mark, already in /images).
 */
export const PROVIDER_ICONS: Record<string, string> = {
  slack: `${BASE}/slack.svg`,
  github: `${BASE}/github.svg`,
  salesforce: `${BASE}/salesforce.svg`,
  // Two Salesforce orgs are connectable at once -- the insurance org and Cowbell
  // Resiliency Services -- so they cannot share a mark or the cards are
  // indistinguishable. Cloud and wordmark are the unmodified official asset; only a
  // CRS label is added.
  salesforce_crs: `${BASE}/salesforce_crs.svg`,
  datadog: `${BASE}/datadog.svg`,
  atlassian_jira: `${BASE}/atlassian_jira.svg`,
  atlassian_confluence: `${BASE}/atlassian_confluence.svg`,
  gmail: `${BASE}/gmail.svg`,
  google_calendar: `${BASE}/google_calendar.svg`,
  google_drive: `${BASE}/google_drive.svg`,
  zoom: `${BASE}/zoom.png`,
  // The official solid mark (CowbellData `docs/mockups/assets/
  // cowbell-bell-blue.svg`, #0057a8, square viewBox).
  //
  // NOT `/images/Cowbell_Icon.svg`: that asset is a knockout design -- its
  // paths carry inner holes and are filled #fff, so it only reads as a mark
  // on a dark background. Recolouring it produces a hollow outline on the
  // light integration cards rather than a solid mark.
  'cyber-pulse': `${BASE}/cyber-pulse.svg`,
};

export interface ProviderIconProps {
  /** Backend provider key, e.g. `google_calendar`. */
  readonly provider: string;
  /** Catalog-supplied logo URL, preferred when it loads. */
  readonly logoUrl?: string;
  /** Used as the image's alt text. */
  readonly displayName?: string;
  /** Rendered box size in px. The box is reserved before the image resolves. */
  readonly size?: number;
  /**
   * `tile` (default) sits the mark on a neutral scheme-aware square, which
   * keeps very light and very dark marks legible in both schemes. `bare` drops
   * that backing for small inline uses such as a badge's leftSection, where a
   * tile would read as a second nested chip.
   */
  readonly variant?: 'tile' | 'bare';
  readonly className?: string;
}

/**
 * The catalog occasionally sends a `logo_url` that isn't a usable image
 * source at all -- e.g. a host-relative path like
 * `/static/integrations/confluence.svg`, which resolves against *this*
 * app's origin instead of wherever the catalog meant it to live, and 404s.
 * The `onError` fallback in `ProviderIcon` below still recovers visually,
 * but the doomed request is pure noise. Only attempt values that are
 * plausibly loadable from here: absolute URLs, or root-relative paths that
 * point at this app's own `/public` (same convention `PROVIDER_ICONS` uses).
 */
function isLoadableSrc(url: string | undefined): url is string {
  if (!url) return false;
  return /^(https?:)?\/\//.test(url) || url.startsWith(BASE);
}

/**
 * One provider's brand mark, with a three-step fallback:
 * `logoUrl` -> bundled asset -> generic plug.
 *
 * The container is a fixed square at `size` regardless of which step wins, so
 * swapping between them never shifts layout.
 */
export function ProviderIcon({
  provider,
  logoUrl,
  displayName,
  size = 40,
  variant = 'tile',
  className,
}: ProviderIconProps) {
  const bundled = PROVIDER_ICONS[provider];
  const sources = [logoUrl, bundled].filter(isLoadableSrc);
  const [attempt, setAttempt] = useState(0);

  // A changed provider/logo means the previous failures no longer apply.
  useEffect(() => setAttempt(0), [provider, logoUrl]);

  const src = sources[attempt];
  const boxStyle = { width: size, height: size };

  if (!src) {
    return (
      <div
        className={`${styles.fallback} ${className ?? ''}`}
        // Scale the glyph with the box so it carries the same visual weight as
        // the brand marks it sits beside, instead of floating small inside it.
        style={{ ...boxStyle, fontSize: Math.round(size * 0.62) }}
        data-provider={provider}
      >
        <FontAwesomeIcon icon={faPlug} />
      </div>
    );
  }

  const boxClass = variant === 'bare' ? styles.bare : styles.box;

  return (
    <div
      className={`${boxClass} ${className ?? ''}`}
      style={boxStyle}
      data-provider={provider}
    >
      <img
        src={src}
        alt={displayName ?? ''}
        className={styles.image}
        onError={() => setAttempt((a) => a + 1)}
      />
    </div>
  );
}
